import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Divider,
  Chip,
  Avatar,
  Radio,
  FormControlLabel
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  ArrowBack as BackIcon
} from '@mui/icons-material';
import { PinterestIcon } from '../../assets/icons/BrandIcons';
import api from '../../config/api';

const PINTEREST_RED = '#E60023';

// Shown after Pinterest OAuth when the signed-in login can also act for business accounts it
// manages (Pinterest Business Access). The user picks which account the connector is for.
const PinterestProfileSelection = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState('');

  const [profiles, setProfiles] = useState([]);
  const [tempId, setTempId] = useState(null);
  const [selectedProfileId, setSelectedProfileId] = useState('');

  useEffect(() => {
    const temp_id = searchParams.get('temp_id');
    if (!temp_id) {
      setError('Invalid session. Please try authenticating again.');
      setLoading(false);
      return;
    }

    setTempId(temp_id);
    loadProfiles(temp_id);
  }, [searchParams]);

  const loadProfiles = async (temp_id) => {
    try {
      setLoading(true);
      const response = await api.get('/api/connectors/pinterest/profiles', {
        params: { temp_id }
      });

      if (response.data.success) {
        setProfiles(response.data.profiles || []);
      } else {
        setError(response.data.message || 'Failed to load Pinterest accounts');
      }
    } catch (err) {
      console.error('Error loading Pinterest accounts:', err);
      setError(err.response?.data?.message || 'Failed to load Pinterest accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    if (!selectedProfileId) {
      setError('Please choose an account to connect');
      return;
    }

    setConnecting(true);
    setError('');

    try {
      const response = await api.post('/api/connectors/pinterest/select', {
        temp_id: tempId,
        profile_id: selectedProfileId
      });

      if (response.data.success) {
        try {
          await api.post('/api/connectors/pinterest/cleanup', { temp_id: tempId });
        } catch (cleanupError) {
          console.warn('Failed to cleanup temp data:', cleanupError);
        }
        navigate('/connectors', {
          state: {
            message: `Successfully connected ${response.data.connector?.name || 'Pinterest'}`,
            severity: 'success'
          }
        });
      } else {
        setError(response.data.message || 'Failed to connect the selected account');
      }
    } catch (err) {
      console.error('Error connecting Pinterest account:', err);
      setError(err.response?.data?.message || 'Failed to connect the selected account');
    } finally {
      setConnecting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 4 }}>
        <CircularProgress size={60} sx={{ mb: 2 }} />
        <Typography variant="h6">Loading Pinterest accounts...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4, p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/connectors')}
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <Box sx={{ color: PINTEREST_RED, mr: 2, display: 'flex', flexShrink: 0 }}>
          <PinterestIcon size={32} color={PINTEREST_RED} />
        </Box>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Choose Pinterest Account
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Your Pinterest login can manage more than one account. Choose the one to connect.
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Select an account
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            The connector will sync this account's ads, boards, pins and analytics.
          </Typography>

          {profiles.length === 0 ? (!error && (
            <Alert severity="warning">
              No Pinterest accounts found. Please try connecting again.
            </Alert>
          )) : (
            <Grid container spacing={2}>
              {profiles.map((profile) => {
                const isSelected = selectedProfileId === profile.profile_id;
                const adAccounts = profile.ad_accounts || [];
                return (
                  <Grid item xs={12} md={6} key={profile.profile_id}>
                    <Card
                      variant="outlined"
                      onClick={() => !connecting && setSelectedProfileId(profile.profile_id)}
                      sx={{
                        height: '100%',
                        cursor: connecting ? 'default' : 'pointer',
                        borderColor: isSelected ? PINTEREST_RED : undefined,
                        transition: 'all 0.2s',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: 4
                        }
                      }}
                    >
                      <CardContent>
                        <FormControlLabel
                          control={
                            <Radio
                              checked={isSelected}
                              onChange={() => setSelectedProfileId(profile.profile_id)}
                              disabled={connecting}
                              color="primary"
                            />
                          }
                          label={
                            <Box sx={{ width: '100%' }}>
                              <Box sx={{ display: 'flex', alignItems: 'start', mb: 2 }}>
                                <Avatar sx={{ bgcolor: PINTEREST_RED, mr: 2 }}>
                                  <PinterestIcon size={20} color="#fff" />
                                </Avatar>
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    {profile.business_name || profile.username}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    @{profile.username}
                                  </Typography>
                                  {profile.alreadyConnected && (
                                    <Box sx={{ mt: 0.5 }}>
                                      <Chip
                                        label={profile.connectedBrandName
                                          ? `Connected · ${profile.connectedBrandName}`
                                          : 'Already connected'}
                                        size="small"
                                        color="success"
                                        variant="outlined"
                                      />
                                    </Box>
                                  )}
                                </Box>
                              </Box>

                              <Divider sx={{ my: 1 }} />

                              <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                <Chip
                                  size="small"
                                  label={profile.is_signed_in_account ? 'Signed in' : 'Business account'}
                                  color={profile.is_signed_in_account ? 'default' : 'primary'}
                                  sx={{ maxWidth: '100%' }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                  {adAccounts.length === 0
                                    ? 'No ad accounts'
                                    : `Ad accounts: ${adAccounts.map(a => a.name || a.id).join(', ')}`}
                                </Typography>
                              </Box>
                            </Box>
                          }
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </CardContent>
      </Card>

      {profiles.length > 0 && (
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/connectors')}
            disabled={connecting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConnect}
            disabled={!selectedProfileId || connecting}
            startIcon={connecting ? <CircularProgress size={20} /> : <CheckIcon />}
            sx={{
              backgroundColor: PINTEREST_RED,
              '&:hover': { backgroundColor: '#BD081C' }
            }}
          >
            {connecting ? 'Connecting...' : 'Connect Account'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default PinterestProfileSelection;
